/**
 * The landing page's own palette.
 *
 * @remarks
 * <b>Deliberately separate from the application theme, and deliberately different from it.</b>
 * Inside the product, cyan is the single interaction colour and nothing else is emphasised -
 * that restraint is what keeps an operations tool readable for eight hours. A landing page has
 * the opposite job: it is seen once, for thirty seconds, by somebody deciding whether to care.
 *
 * So this carries the violet-to-blue identity the product owner chose for the public page,
 * while keeping the same near-black blue ground the application sits on. The two meet at the
 * sign-in page, which belongs to the application.
 *
 * Kept in its own module rather than added to the MUI theme because nothing else in the product
 * may reach for these: a violet button appearing on a task page would break the rule the
 * application palette exists to enforce.
 */
export const landing = {
  /** The ground. A shade deeper than the application's, so the glows have somewhere to sit. */
  base: '#080C1F',
  deep: '#05081A',

  /** Card and panel surfaces, a lift above the ground rather than a different colour. */
  surface: 'rgba(20, 28, 56, 0.55)',
  surfaceSolid: '#111A36',

  border: 'rgba(108, 128, 196, 0.18)',
  borderBright: 'rgba(138, 160, 255, 0.35)',

  /** The identity pair. Violet leads, blue answers. */
  violet: '#7C5CFF',
  violetSoft: '#A78BFA',
  blue: '#4DA6FF',
  cyan: '#38BDF8',

  /** Feature accents. Each card gets one, so five icons do not read as one block. */
  mint: '#2DD4A7',
  amber: '#FBBF24',

  text: '#EAF0FF',
  textMuted: '#9FB0D9',
  textFaint: '#6F82AE',
} as const;

/** The violet-to-blue sweep used on the primary action and the headline's second line. */
export const landingGradient = `linear-gradient(90deg, ${landing.violet} 0%, ${landing.blue} 100%)`;

/**
 * The ambient glow behind the hero.
 *
 * @remarks
 * Two soft radial pools rather than an image: it costs nothing to download, survives any
 * viewport, and cannot be the thing that makes a first paint slow.
 */
export const landingGlow =
  `radial-gradient(900px 520px at 78% 12%, rgba(124, 92, 255, 0.22), transparent 62%), ` +
  `radial-gradient(760px 480px at 12% 38%, rgba(77, 166, 255, 0.16), transparent 60%)`;
